import Link from '@amzn/meridian/link';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import Pagination from '@amzn/meridian/pagination';
import moment from 'moment';
import React, { useContext, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { isFeatureEnabled, Feature } from '../../../../config/FeaturesConfig';
import { renderUrl, urls } from '../../../../config/Url';
import { useNavigate } from 'react-router-dom';
import PerformancePortalContext from '../../../PerformancePortalContext';
import useCustomTracking from '../../../../Util/useCustomTracking';
import noDataPic from '../../../../Assets/NoDataLarge.svg';
import Column from '@amzn/meridian/column';
import {
  SortColumn,
  ExtendedContacts,
} from '../../DashboardComponents/HmdDetails/types';
import { customSort } from '../../DashboardComponents/HmdDetails/helper';
import { contactTypes } from '../HmdDetails/DashboardDetails';
import ContactListeningModal from '../../../ContactListening/ContactListeningModal';
import ContactListening from '../../../ContactListening/ContactListening';
import {
  ContactData,
  defaultContactData,
} from '../../../../api/ContactListening/schema';
import DataState from '../../../../Types/DataState';
import { StateContext } from '../../../../GlobalStateStore';
import { CLClickstream } from '../../../ContactListening/utils/ContactListeningHelpers';
import { QueryVariablesContactListeningData } from '../../../../api/Naws';
import { ContactStatusOptionsObject } from '../../../../api/CoachingForm/schema';

import Storage from '../../../../Util/Storage';

interface ContactsProps {
  contacts: ExtendedContacts[];
  isHMDRealTime: boolean;
  hmdTab?: string;
  contactStartDate: string;
  contactEndDate: string;
  userId: string;
}

const itemsPerPage = 20;

const Contacts: React.FC<ContactsProps> = ({
  contacts,
  isHMDRealTime,
  hmdTab,
  contactStartDate,
  contactEndDate,
  userId,
}: ContactsProps): React.ReactElement => {
  const contactLinksEnabled = isFeatureEnabled(Feature.CONTACT_LINKS);
  const contactListeningEnabled = isFeatureEnabled(Feature.CONTACT_LISTENING);
  const enableReviewRealTimeHmd =
    isHMDRealTime && isFeatureEnabled(Feature.REVIEW_REAL_TIME_HMD);
  const navigate = useNavigate();
  const { performancePortalContextValue, setPerformancePortalContextValue } =
    useContext(PerformancePortalContext);
  const [sortColumn, setSortColumn] = useState<keyof SortColumn & string>(
    'alias'
  );
  const [sortDirection, setSortDirection] = useState('ascending');
  // eslint-disable-next-line no-shadow
  const onSort = useCallback(
    ({
      sortColumn,
      sortDirection,
    }: {
      sortColumn: keyof SortColumn & string;
      sortDirection: string;
    }) => {
      setSortDirection(sortDirection);
      setSortColumn(sortColumn);
    },
    []
  );

  const [currentPage, setCurrentPage] = useState(1);
  const firstVisibleIndex = (currentPage - 1) * itemsPerPage;
  const lastVisibleIndex = firstVisibleIndex + itemsPerPage;
  const numberOfPages = Math.ceil(contacts.length / itemsPerPage);

  const [TableSortTrackingTag, tableSortClick] = useCustomTracking(
    'dashboard-contacts-table-sort',
    'dashboard-contacts-table-sort',
    {
      'data-csa-c-type': 'table',
      'data-csa-c-sort-column': sortColumn || '',
      'data-csa-c-sort-order': sortDirection || '',
    }
  );

  let parsedContacts: ExtendedContacts[] = JSON.parse(JSON.stringify(contacts));

  parsedContacts.sort((a: ExtendedContacts, b: ExtendedContacts) => {
    return customSort(a, b, sortColumn, sortDirection);
  });

  useEffect(() => {
    tableSortClick();
  }, [sortColumn, sortDirection]);

  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [recordingContactId, setRecordingContactId] = useState('');
  const [contactListeningData, setContactListeningData] =
    useState<ContactData>(defaultContactData);
  const [contactListeningDataState, setContactListeningDataState] =
    useState<string>(DataState.INITIALIZED);
  const [contactListeningParams, setContactListeningParams] =
    useState<QueryVariablesContactListeningData>();

  let clickstreamTags = {};
  let tag3: string | undefined;
  let tag4: string | undefined;
  if (hmdTab) {
    if (hmdTab === contactTypes.hmdYes) {
      if (isHMDRealTime) {
        tag3 = 'YesTab';
      } else {
        tag4 = 'HMDdetailsYes';
      }
    } else if (hmdTab === contactTypes.hmdNo) {
      if (isHMDRealTime) {
        tag3 = 'NoTab';
      } else {
        tag4 = 'HMDdetailsNo';
      }
    } else if (hmdTab === contactTypes.repeat) {
      if (isHMDRealTime) {
        tag3 = 'RepeatTab';
      } else {
        tag4 = 'HMDdetailsRepeat';
      }
    }
  }
  if (isHMDRealTime) {
    clickstreamTags = {
      ...clickstreamTags,
      'data-csa-c-tag2': 'RealTimeHMD',
      'data-csa-c-tag3': tag3,
    };
  } else {
    clickstreamTags = {
      ...clickstreamTags,
      'data-csa-c-tag2': 'Metrics',
      'data-csa-c-tag3': 'RAP',
      'data-csa-c-tag4': tag4,
    };
  }

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { cscEntityPrefix, isTm } = inspireOptions.data;

  const CLClickstream: CLClickstream = {
    slotID: 'open-contact-listening-modal',
    tag1: 'Dashboard',
    tag2: isHMDRealTime ? 'RealTimeHMD' : 'Metrics',
    tag3: isHMDRealTime ? tag3 : 'RAP',
    tag4: tag4,
  };

  const mapContactStatus = (status: string | null): React.ReactNode | null => {
    if (!status) {
      return;
    }

    const defaultMessage =
      status[0].toUpperCase() +
      status.substring(1, status.length).toLowerCase();

    let id = '';
    switch (status) {
      case ContactStatusOptionsObject.DELIVERED:
        id = 'cs_perfportal_status_delivered';
        break;
      case ContactStatusOptionsObject.DRAFT:
        id = 'cs_perfportal_coaching_status_not_coached';
        break;
      case ContactStatusOptionsObject.REVIEWED:
      default:
        id = 'cs_perfportal_contact_recom_reviewed_status';
        break;
    }

    return <FormattedMessage defaultMessage={defaultMessage} id={id} />;
  };

  const getFormattedMessageFromContactStatus = (
    item: ExtendedContacts
  ): React.ReactNode => {
    let id = 'cs_perfportal_coaching_review';
    let defaultMessage = 'Review';

    switch (item.contactStatus) {
      case ContactStatusOptionsObject.DRAFT:
        id = id + '_edit';
        defaultMessage = 'Edit';
        break;
      case ContactStatusOptionsObject.DELIVERED:
      case ContactStatusOptionsObject.REVIEWED:
        id = id + '_view';
        defaultMessage = 'View';
        break;
    }
    return <FormattedMessage id={id} defaultMessage={defaultMessage} />;
  };

  const handleReviewClick = async (contact: ExtendedContacts) => {
    if (performancePortalContextValue && setPerformancePortalContextValue) {
      await Storage.setItem(
        'realTimeHMDContactForReview',
        JSON.stringify({
          communicationDate: contact.communicationDate,
          issueCode: contact.issueCode,
          channelCode: contact.channelCode,
        })
      );
      setPerformancePortalContextValue({
        ...performancePortalContextValue,
        passedCCXUserID: userId,
      });
      navigate(
        renderUrl(
          `${urls.contactReview}/${contact.userID}/${contact.contactID}?ingress=realtimeHMD&pUserID=${userId}&hmdTab=${hmdTab}`
        )
      );
    }
  };

  return parsedContacts.length === 0 ? (
    <>
      <Column width='100%' heights='fill' alignmentHorizontal='center'>
        <Box>
          <img src={noDataPic} alt='No data' />
        </Box>
        <Box>
          <Text type='h200'>
            <FormattedMessage
              id='cs_perfportal_no_metrics'
              defaultMessage='We have not received any metrics this week.'
            />
          </Text>
        </Box>
      </Column>
    </>
  ) : (
    <>
      <ContactListeningModal
        loadingState={contactListeningDataState}
        showModal={showRecordingModal}
        setShowModal={setShowRecordingModal}
        contactId={recordingContactId}
        contactListeningData={contactListeningData}
        contactListeningParams={contactListeningParams}
        CLClickstream={{
          ...CLClickstream,
          tag5: 'Contact Listening Modal',
        }}
      />
      <Column alignmentHorizontal='center'>
        <Table
          headerRows={1}
          sortColumn={sortColumn}
          sortDirection={
            sortDirection === 'ascending' ? 'ascending' : 'descending'
          }
          onSort={onSort}
          showStripes
          showDividers
        >
          <TableSortTrackingTag />
          <TableRow highlightOnHover={false}>
            <TableCell sortColumn='alias'>
              <FormattedMessage
                id='cs_perfportal_hmdDetails_alias'
                defaultMessage='Alias'
              />
            </TableCell>
            <TableCell sortColumn='channelCode'>
              <FormattedMessage
                id='PerfPortal_channel'
                defaultMessage='Channel'
              />
            </TableCell>
            <TableCell sortColumn='workCategory'>
              <FormattedMessage
                id='PerfPortal_workCategory'
                defaultMessage='Work Category'
              />
            </TableCell>
            <TableCell sortColumn='issueCode'>
              <FormattedMessage
                id='PerfPortal_issueCode'
                defaultMessage='Issue Code'
              />
            </TableCell>
            <TableCell sortColumn='communicationDate'>
              <FormattedMessage
                id='cs_perfportal_communication_date'
                defaultMessage='Communication Date'
              />
            </TableCell>
            <TableCell sortColumn='responseDate'>
              <FormattedMessage
                id='cs_perfportal_response_date'
                defaultMessage='Response Date'
              />
            </TableCell>
            <TableCell>
              <FormattedMessage
                id='PerfPortal_contactId'
                defaultMessage='Contact ID'
              />
            </TableCell>

            <TableCell>
              <FormattedMessage
                id='PerfPortal_repeatContactId'
                defaultMessage='Repeat Contact Id'
              />
            </TableCell>
            {isTm && enableReviewRealTimeHmd && (
              <TableCell sortColumn='contactStatus'>
                <FormattedMessage
                  id='cs_perfportal_contact_status'
                  defaultMessage='Contact Status'
                />
              </TableCell>
            )}
            {isTm && enableReviewRealTimeHmd && (
              <TableCell>
                <FormattedMessage
                  id='cs_perfportal_action'
                  defaultMessage='Action'
                />
              </TableCell>
            )}
          </TableRow>
          {parsedContacts
            .slice(firstVisibleIndex, lastVisibleIndex)
            .map((metricContact, index) => {
              const {
                channelCode,
                workCategory,
                issueCode,
                communicationDate,
                responseDate,
                contactID,
                rapRepeatContactId,
                marketplaceName,
                alias,
                userID,
                encryptedRapRepeatContactId,
                contactStatus,
              } = metricContact;

              const idOnly = userID.includes(':')
                ? userID.split(':')[1]
                : userID;

              const contactHandlerId = `${cscEntityPrefix}:${idOnly}`;
              const aliasUrl = renderUrl(`${urls.dashboard}/${idOnly}`);
              const contactUrl = `/gp/stores/${marketplaceName}/gp/communications/issue-manager?initialCommId=${contactID}`;
              const repeatContactUrl = `/gp/stores/${marketplaceName}/gp/communications/issue-manager?initialCommId=${encryptedRapRepeatContactId}`;
              return (
                <TableRow
                  key={alias + communicationDate + currentPage + index}
                  highlightOnHover={false}
                >
                  <TableCell>
                    <Text>
                      <Link
                        {...clickstreamTags}
                        data-csa-c-type='link'
                        data-csa-c-slot-id='open-associate-profile-link'
                        data-csa-c-content-id={`open-associate-profile-link-for-${userID}`}
                        data-csa-c-tag1='Dashboard'
                        data-csa-c-profile-id={`${userID}`}
                        href={aliasUrl}
                        onClick={navigate}
                        type='secondary'
                      >
                        {alias}
                      </Link>
                    </Text>
                  </TableCell>
                  <TableCell>{channelCode}</TableCell>
                  <TableCell>{workCategory}</TableCell>
                  <TableCell>{issueCode}</TableCell>
                  <TableCell>
                    {moment(communicationDate)
                      .utc()
                      .format('YYYY-MM-DD HH:mm') !== '1970-01-01 00:00'
                      ? isHMDRealTime
                        ? moment(communicationDate)
                            .utc()
                            .format('YYYY-MM-DD HH:mm') + ' UTC'
                        : moment(communicationDate).format('YYYY-MM-DD HH:mm')
                      : '-'}{' '}
                  </TableCell>
                  <TableCell>
                    {isHMDRealTime
                      ? moment(responseDate).utc().format('YYYY-MM-DD HH:mm') +
                        ' UTC'
                      : moment(responseDate).format('YYYY-MM-DD HH:mm')}
                  </TableCell>

                  <TableCell>
                    {(contactLinksEnabled && marketplaceName) ||
                    contactListeningEnabled ? (
                      <ContactListening
                        contactID={contactID}
                        setContactListening={setContactListeningData}
                        setContactListeningState={setContactListeningDataState}
                        setRecordingContactId={setRecordingContactId}
                        setShowRecordingModal={setShowRecordingModal}
                        setContactListeningParams={setContactListeningParams}
                        clickstreamTags={{
                          ...clickstreamTags,
                          'data-csa-c-slot-id': 'open-contact-listening-link',
                          'data-csa-c-content-id': `open-contact-listening-link-for-${contactID}`,
                          'data-csa-c-tag1': 'Dashboard',
                          'data-csa-c-profile-id': userID,
                          'data-csa-c-contactid': contactID,
                        }}
                        cscEndpoint={contactUrl}
                        cscTarget='_blank'
                        contactStartDate={contactStartDate}
                        contactEndDate={contactEndDate}
                        contactHandlerId={contactHandlerId}
                        CLClickstream={CLClickstream}
                      />
                    ) : (
                      contactID
                    )}
                  </TableCell>
                  <TableCell>
                    {(contactListeningEnabled ||
                      (contactLinksEnabled &&
                        marketplaceName &&
                        encryptedRapRepeatContactId !== null)) &&
                    rapRepeatContactId !== null ? (
                      <ContactListening
                        contactID={rapRepeatContactId}
                        setContactListening={setContactListeningData}
                        setContactListeningState={setContactListeningDataState}
                        setRecordingContactId={setRecordingContactId}
                        setShowRecordingModal={setShowRecordingModal}
                        setContactListeningParams={setContactListeningParams}
                        clickstreamTags={{
                          ...clickstreamTags,
                          'data-csa-c-slot-id':
                            'open-repeat-contact-listening-link',
                          'data-csa-c-content-id': `open-repeat-contact-listening-link-for-${rapRepeatContactId}`,
                          'data-csa-c-tag1': 'Dashboard',
                          'data-csa-c-profile-id': `${userID}`,
                          'data-csa-c-contactid': rapRepeatContactId,
                        }}
                        cscEndpoint={repeatContactUrl}
                        cscTarget='_blank'
                        contactStartDate={contactStartDate}
                        contactEndDate={contactEndDate}
                        contactHandlerId={contactHandlerId}
                        isRepeatContact={true}
                        repeatEncryptedContactID={encryptedRapRepeatContactId!}
                        CLClickstream={CLClickstream}
                      />
                    ) : (
                      rapRepeatContactId
                    )}
                  </TableCell>
                  {isTm && enableReviewRealTimeHmd && (
                    <TableCell>
                      <Text>{mapContactStatus(contactStatus)}</Text>
                    </TableCell>
                  )}
                  {isTm && enableReviewRealTimeHmd && (
                    <TableCell>
                      <Link
                        type='secondary'
                        data-csa-c-type='link'
                        data-csa-c-content-id={`hmd-${getFormattedMessageFromContactStatus(
                          metricContact
                        )}-link`}
                        data-csa-c-is-tm={isTm}
                        data-csa-c-slot-id={`hmd-${getFormattedMessageFromContactStatus(
                          metricContact
                        )}-link-slot`}
                        data-csa-c-profile-id={userID}
                        data-csa-c-username={alias}
                        data-csa-c-tag1='Dashboard'
                        data-csa-c-tag2={
                          isHMDRealTime ? 'RealTimeHMD' : 'Metrics'
                        }
                        data-cy={`action-link-${metricContact.contactStatus}-contactId-${metricContact.contactID}`}
                        data-csa-c-action={getFormattedMessageFromContactStatus(
                          metricContact
                        )}
                        onClick={() => handleReviewClick(metricContact)}
                      >
                        {getFormattedMessageFromContactStatus(metricContact)}
                      </Link>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
        </Table>
        <Pagination
          showSkipArrows={true}
          numberOfPages={numberOfPages}
          onChange={setCurrentPage}
          currentPage={currentPage}
        />
      </Column>
    </>
  );
};

export default Contacts;
